"use client";

import Image from "next/image";
import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import { IMConfigTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { useTranslations } from "@/app/lib/LocaleProvider";
import { useAIFooterFeedback } from "./useAIFooterFeedback";
import styles from "./index.module.scss";
import Satisfy from "./Satisfy";
import { Feedback } from "@/app/store/types";

export interface AIFooterData {
  parentContentInfo?: {
    parentContentId?: string;
    parentContent?: string;
    parentContentType?: ReportParentContentEnum;
  };
  [key: string]: unknown;
}

export interface AIFooterProps {
  feedback?: Feedback;
  data: AIFooterData;
  requestId?: string;
  content?: string;
  configType: IMConfigTypeEnum;
  onLike?: () => void;
  onDislike?: () => void;
  /** 是否渲染整个 footer，为 false 时 return null */
  showFooter?: boolean;
  /** 是否渲染操作区（复制/点赞/点踩） */
  showActions?: boolean;
  /** 是否显示复制按钮（由调用方根据 content / aiTypingEnd 等计算） */
  showCopy?: boolean;
  /** 状态区类型：正常文案 / 错误态（含重做按钮） */
  statusType?: "normal" | "error";
  /** 状态区主文案 */
  statusText: string;
  /** 错误态时点击重做 */
  onRedo?: () => void;
  /** 是否显示满意度评价 */
  showSatisfy?: boolean;
  /** 流式消息ID */
  streamingId?: string;
}

/**
 * 统一的 AI 消息底部栏：状态文案 + 复制/点赞/点踩
 * Feed 与 TechAI 共用同一 UI 与交互，仅通过 props 区分展示与配置。
 */
export function AIFooter({
  feedback,
  data,
  requestId,
  content,
  configType,
  streamingId,
  onLike,
  onDislike,
  showFooter = true,
  showActions = true,
  showCopy = false,
  statusType = "normal",
  showSatisfy = false,
  statusText,
  onRedo,
}: AIFooterProps) {
  const t = useTranslations();
  const parentContentInfo = data?.parentContentInfo || {};

  const { handleCopy, ...rest } = useAIFooterFeedback({
    parentContentInfo,
    requestId,
    configType,
    content,
    onLike,
    onDislike,
    streamingId,
  });

  const handleRedo = () => onRedo?.();

  if (!showFooter) return null;

  return (
    <>
      <div className={styles.tips}>
        <div className={styles.statusText}>
          {statusType === "error" ? (
            <div className={styles.suspendedWrapper}>
              <span className={styles.red}>{statusText}</span>
              <button
                className={styles.redoWrapper}
                onClick={handleRedo}
                aria-label="重新生成"
              >
                <Image src="/images/redo.svg" height={20} width={20} alt="" />
              </button>
            </div>
          ) : (
            <span>{statusText}</span>
          )}
        </div>

        {showActions && (
          <div className={styles.iconWrapper}>
            {statusType === "normal" && (
              <>
                {showCopy && (
                  <div
                    className={styles.copy}
                    onClick={handleCopy}
                    role="button"
                    tabIndex={0}
                    aria-label={t.chat.copy}
                  >
                    <Image
                      src="/images/copy.svg"
                      height={20}
                      width={20}
                      alt={t.chat.copy}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showSatisfy && (
        <Satisfy
          feedback={feedback}
          streamingId={streamingId}
          requestId={requestId}
          parentContentInfo={
            parentContentInfo as {
              parentContentId: string;
              parentContent: string;
              parentContentType: ReportParentContentEnum;
            }
          }
          configType={configType}
          {...rest}
        />
      )}
    </>
  );
}
